import {
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassInput,
  GlassModal,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import {
  AlertCircle,
  Building2,
  Loader2,
  Mail,
  Phone,
  Plus,
  Search,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { useState } from 'react';

import { useGraphQL } from '../hooks/useGraphQL.js';
import { useGraphQLMutation } from '../hooks/useGraphQLMutation.js';
import { usePortalAuth } from '../hooks/usePortalAuth.js';

import type { ReactElement } from 'react';

/* ── GraphQL ── */

const GET_CONTACTS = `
  query GetUnitContacts($first: Int) {
    unitContacts(first: $first) {
      edges {
        node {
          id
          firstName
          lastName
          email
          phone
          contactType
          propertyId
          unitAk
          active
          property { id name }
        }
      }
    }
  }
`;

const GET_PROPERTIES = `
  query GetProperties($first: Int) {
    properties(first: $first) {
      edges {
        node { id name clientCode }
      }
    }
  }
`;

const CREATE_CONTACT = `
  mutation CreateUnitContact(
    $clientCode: String!
    $propertyId: Int!
    $unitAk: Float
    $contactType: String!
    $firstName: String!
    $lastName: String!
    $email: String
    $phone: String
  ) {
    createUnitContact(
      clientCode: $clientCode
      propertyId: $propertyId
      unitAk: $unitAk
      contactType: $contactType
      firstName: $firstName
      lastName: $lastName
      email: $email
      phone: $phone
    ) { id firstName lastName }
  }
`;

const UPDATE_CONTACT = `
  mutation UpdateUnitContact(
    $id: Int!
    $firstName: String
    $lastName: String
    $email: String
    $phone: String
    $contactType: String
    $active: Int
  ) {
    updateUnitContact(
      id: $id
      firstName: $firstName
      lastName: $lastName
      email: $email
      phone: $phone
      contactType: $contactType
      active: $active
    ) { id firstName lastName }
  }
`;

/* ── Types ── */

type ContactType = 'property_manager' | 'unit_owner' | 'resident';
type ContactTypeFilter = 'all' | ContactType;
type GlassBadgeVariant = 'info' | 'success' | 'warning' | 'danger' | 'default';

interface ContactNode {
  id: number;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  contactType: string;
  propertyId: number | null;
  unitAk: number | null;
  active: number;
  property: { id: number; name: string } | null;
}

interface PropertyNode {
  id: number;
  name: string;
  clientCode: string;
}

interface ContactsData {
  unitContacts: { edges: { node: ContactNode }[] };
}

interface PropertiesData {
  properties: { edges: { node: PropertyNode }[] };
}

interface EditForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  contactType: string;
}

interface CreateForm extends EditForm {
  propertyId: string;
}

/* ── Constants ── */

const CONTACT_TYPE_FILTER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'property_manager', label: 'Property Manager' },
  { value: 'unit_owner', label: 'Unit Owner' },
  { value: 'resident', label: 'Resident' },
];

const CONTACT_TYPE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'property_manager', label: 'Property Manager' },
  { value: 'unit_owner', label: 'Unit Owner' },
  { value: 'resident', label: 'Resident' },
];

const contactTypeLabel: Record<string, string> = {
  property_manager: 'Property Manager',
  unit_owner: 'Unit Owner',
  resident: 'Resident',
};

const contactTypeBadgeVariant: Record<string, GlassBadgeVariant> = {
  property_manager: 'info',
  unit_owner: 'success',
  resident: 'default',
};

/* ── Helpers ── */

function fullName(node: ContactNode): string {
  return `${node.firstName} ${node.lastName}`.trim();
}

function matchesSearch(node: ContactNode, query: string): boolean {
  if (query === '') {
    return true;
  }
  const q = query.toLowerCase();
  return (
    fullName(node).toLowerCase().includes(q) ||
    (node.email?.toLowerCase().includes(q) ?? false) ||
    (node.phone?.toLowerCase().includes(q) ?? false) ||
    (node.property?.name.toLowerCase().includes(q) ?? false)
  );
}

/* ── Sub-components ── */

interface ContactCardProps {
  contact: ContactNode;
  onEdit: (contact: ContactNode) => void;
}

function ContactCard({ contact, onEdit }: ContactCardProps): ReactElement {
  const variant = contactTypeBadgeVariant[contact.contactType] ?? 'default';
  const label = contactTypeLabel[contact.contactType] ?? contact.contactType;

  return (
    <GlassCard
      hover
      className="cursor-pointer"
      onClick={() => {
        onEdit(contact);
      }}
    >
      <GlassCardContent className="py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-medium text-sm text-text">{fullName(contact)}</span>
              <GlassBadge variant={variant}>{label}</GlassBadge>
              {contact.active === 0 && <GlassBadge variant="danger">Inactive</GlassBadge>}
            </div>
            {contact.email !== null && (
              <p className="text-xs text-text-secondary flex items-center gap-1">
                <Mail size={11} className="shrink-0" />
                {contact.email}
              </p>
            )}
            {contact.phone !== null && (
              <p className="text-xs text-text-secondary flex items-center gap-1 mt-0.5">
                <Phone size={11} className="shrink-0" />
                {contact.phone}
              </p>
            )}
            {contact.property !== null && (
              <p className="text-xs text-text-tertiary flex items-center gap-1 mt-0.5">
                <Building2 size={11} className="shrink-0" />
                {contact.property.name}
              </p>
            )}
          </div>
          <GlassButton
            variant="ghost"
            size="sm"
            aria-label={`Edit ${fullName(contact)}`}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(contact);
            }}
          >
            Edit
          </GlassButton>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Edit Modal ── */

interface EditModalProps {
  contact: ContactNode;
  onClose: () => void;
  onSave: (id: number, form: EditForm) => Promise<void>;
  saving: boolean;
  saveError: string | null;
}

function EditModal({ contact, onClose, onSave, saving, saveError }: EditModalProps): ReactElement {
  const [form, setForm] = useState<EditForm>({
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    contactType: contact.contactType,
  });

  function patch(field: keyof EditForm, value: string): void {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function handleSubmit(): void {
    void onSave(contact.id, form);
  }

  return (
    <GlassModal open onClose={onClose} className="max-w-lg">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-base font-semibold text-text">Edit Contact</h2>
        <GlassButton variant="ghost" size="sm" onClick={onClose} aria-label="Close">
          <X size={14} />
        </GlassButton>
      </div>

      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <GlassInput
            label="First Name"
            value={form.firstName}
            onChange={(e) => {
              patch('firstName', e.target.value);
            }}
          />
          <GlassInput
            label="Last Name"
            value={form.lastName}
            onChange={(e) => {
              patch('lastName', e.target.value);
            }}
          />
        </div>
        <GlassInput
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => {
            patch('email', e.target.value);
          }}
          placeholder="name@example.com"
        />
        <GlassInput
          label="Phone"
          type="tel"
          value={form.phone}
          onChange={(e) => {
            patch('phone', e.target.value);
          }}
          placeholder="(555) 000-0000"
        />
        <GlassSelect
          label="Contact Type"
          options={CONTACT_TYPE_OPTIONS}
          value={form.contactType}
          onChange={(e) => {
            patch('contactType', e.target.value);
          }}
        />

        {saveError !== null && (
          <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
            <AlertCircle size={13} className="mt-0.5 shrink-0" />
            <span>{saveError}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-1">
          <GlassButton variant="secondary" size="md" onClick={onClose} disabled={saving}>
            Cancel
          </GlassButton>
          <GlassButton variant="primary" size="md" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                Saving&hellip;
              </>
            ) : (
              'Save Changes'
            )}
          </GlassButton>
        </div>
      </div>
    </GlassModal>
  );
}

/* ── Create Modal ── */

interface CreateModalProps {
  properties: PropertyNode[];
  clientCode: string;
  onClose: () => void;
  onSave: (form: CreateForm) => Promise<void>;
  saving: boolean;
  saveError: string | null;
}

function CreateModal({
  properties,
  clientCode,
  onClose,
  onSave,
  saving,
  saveError,
}: CreateModalProps): ReactElement {
  const [form, setForm] = useState<CreateForm>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    contactType: 'unit_owner',
    propertyId: properties[0] !== undefined ? String(properties[0].id) : '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CreateForm, string>>>({});

  const propertyOptions: readonly { value: string; label: string }[] = properties.map((p) => ({
    value: String(p.id),
    label: p.name,
  }));

  function patch(field: keyof CreateForm, value: string): void {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field] !== undefined) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  }

  function validate(): boolean {
    const next: Partial<Record<keyof CreateForm, string>> = {};
    if (form.firstName.trim() === '') {
      next.firstName = 'Required';
    }
    if (form.lastName.trim() === '') {
      next.lastName = 'Required';
    }
    if (form.propertyId === '') {
      next.propertyId = 'Select a property';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(): void {
    if (!validate()) {
      return;
    }
    void onSave({ ...form, clientCode } as CreateForm & { clientCode: string });
  }

  return (
    <GlassModal open onClose={onClose} className="max-w-lg">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-base font-semibold text-text">Add Contact</h2>
        <GlassButton variant="ghost" size="sm" onClick={onClose} aria-label="Close">
          <X size={14} />
        </GlassButton>
      </div>

      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <GlassInput
            label="First Name"
            value={form.firstName}
            onChange={(e) => {
              patch('firstName', e.target.value);
            }}
            error={errors.firstName ?? ''}
          />
          <GlassInput
            label="Last Name"
            value={form.lastName}
            onChange={(e) => {
              patch('lastName', e.target.value);
            }}
            error={errors.lastName ?? ''}
          />
        </div>
        <GlassInput
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => {
            patch('email', e.target.value);
          }}
          placeholder="name@example.com"
        />
        <GlassInput
          label="Phone"
          type="tel"
          value={form.phone}
          onChange={(e) => {
            patch('phone', e.target.value);
          }}
          placeholder="(555) 000-0000"
        />
        <GlassSelect
          label="Contact Type"
          options={CONTACT_TYPE_OPTIONS}
          value={form.contactType}
          onChange={(e) => {
            patch('contactType', e.target.value);
          }}
        />
        <GlassSelect
          label="Property"
          options={propertyOptions}
          value={form.propertyId}
          onChange={(e) => {
            patch('propertyId', e.target.value);
          }}
          placeholder="Select a property"
          error={errors.propertyId ?? ''}
        />

        {saveError !== null && (
          <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-xs text-danger">
            <AlertCircle size={13} className="mt-0.5 shrink-0" />
            <span>{saveError}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-1">
          <GlassButton variant="secondary" size="md" onClick={onClose} disabled={saving}>
            Cancel
          </GlassButton>
          <GlassButton variant="primary" size="md" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 size={13} className="animate-spin" />
                Adding&hellip;
              </>
            ) : (
              'Add Contact'
            )}
          </GlassButton>
        </div>
      </div>
    </GlassModal>
  );
}

/* ── Page ── */

export function ContactsPage(): ReactElement {
  const user = usePortalAuth((s) => s.user);
  const clientCode = user?.clientCode ?? '';

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ContactTypeFilter>('all');
  const [editContact, setEditContact] = useState<ContactNode | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const {
    data: contactsData,
    loading: contactsLoading,
    error: contactsError,
    refetch,
  } = useGraphQL<ContactsData>(GET_CONTACTS, { first: 100 });

  const { data: propsData, loading: propsLoading } = useGraphQL<PropertiesData>(GET_PROPERTIES, {
    first: 100,
  });

  const {
    mutate: updateContact,
    loading: updateLoading,
    error: updateError,
  } = useGraphQLMutation<unknown>(UPDATE_CONTACT);

  const {
    mutate: createContact,
    loading: createLoading,
    error: createError,
  } = useGraphQLMutation<unknown>(CREATE_CONTACT);

  const allContacts: ContactNode[] = contactsData?.unitContacts.edges.map((e) => e.node) ?? [];

  const properties: PropertyNode[] = propsData?.properties.edges.map((e) => e.node) ?? [];

  const filtered = allContacts.filter((c) => {
    const matchesType = typeFilter === 'all' || c.contactType === typeFilter;
    return matchesType && matchesSearch(c, search);
  });

  function flash(msg: string): void {
    setSuccessMsg(msg);
    setTimeout(() => {
      setSuccessMsg(null);
    }, 3500);
  }

  async function handleEditSave(id: number, form: EditForm): Promise<void> {
    const result = await updateContact({
      id,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email !== '' ? form.email : null,
      phone: form.phone !== '' ? form.phone : null,
      contactType: form.contactType,
    });
    if (result !== null) {
      setEditContact(null);
      flash('Contact updated successfully.');
      refetch();
    }
  }

  async function handleCreateSave(form: CreateForm & { clientCode?: string }): Promise<void> {
    const result = await createContact({
      clientCode,
      propertyId: parseInt(form.propertyId, 10),
      contactType: form.contactType,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email !== '' ? form.email : null,
      phone: form.phone !== '' ? form.phone : null,
    });
    if (result !== null) {
      setShowCreate(false);
      flash('Contact added successfully.');
      refetch();
    }
  }

  /* ── Render ── */

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text">Contacts</h1>
          <p className="text-sm text-text-secondary mt-0.5">
            {contactsLoading
              ? 'Loading contacts\u2026'
              : `${String(allContacts.length)} contact${allContacts.length !== 1 ? 's' : ''} across your properties`}
          </p>
        </div>
        <GlassButton
          variant="primary"
          size="md"
          onClick={() => {
            setShowCreate(true);
          }}
          disabled={propsLoading}
        >
          <Plus size={14} />
          Add Contact
        </GlassButton>
      </div>

      {/* Success banner */}
      {successMsg !== null && (
        <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-4 py-2.5 text-sm text-success-fg">
          <UserCheck size={15} className="shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {/* Error banner */}
      {contactsError !== null && (
        <div className="flex items-start gap-3 rounded-lg border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium">Failed to load contacts</p>
            <p className="text-xs mt-0.5 text-danger/80">{contactsError}</p>
          </div>
          <GlassButton variant="ghost" size="sm" onClick={refetch} className="shrink-0 text-danger">
            Retry
          </GlassButton>
        </div>
      )}

      {/* Filter bar */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-base font-semibold text-text">
              All Contacts
              {!contactsLoading && (
                <span className="ml-2 text-sm font-normal text-text-secondary">
                  ({String(filtered.length)} shown)
                </span>
              )}
            </h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search
                  size={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none"
                />
                <GlassInput
                  placeholder="Search contacts..."
                  value={search}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSearch(e.target.value);
                  }}
                  className="pl-8 w-full sm:w-56"
                  aria-label="Search contacts"
                />
              </div>
              <GlassSelect
                options={CONTACT_TYPE_FILTER_OPTIONS}
                value={typeFilter}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  setTypeFilter(e.target.value as ContactTypeFilter);
                }}
                aria-label="Filter by type"
                className="w-full sm:w-44"
              />
            </div>
          </div>
        </GlassCardHeader>

        {/* Loading state */}
        {contactsLoading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Loader2 size={32} className="text-text-tertiary animate-spin" />
            <p className="text-sm text-text-secondary">Loading contacts&hellip;</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <Users size={40} className="text-text-tertiary" />
            <div className="text-center">
              <p className="text-sm font-medium text-text">No contacts found</p>
              <p className="text-xs text-text-secondary mt-1">
                {search !== '' || typeFilter !== 'all'
                  ? 'Try adjusting your search or filter.'
                  : 'No contacts have been added yet.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Table — desktop */}
            <div className="hidden sm:block">
              <GlassTable>
                <GlassTableHead>
                  <GlassTableRow>
                    <GlassTableHeaderCell>Name</GlassTableHeaderCell>
                    <GlassTableHeaderCell>Type</GlassTableHeaderCell>
                    <GlassTableHeaderCell className="hidden md:table-cell">
                      Email
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="hidden lg:table-cell">
                      Phone
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell className="hidden xl:table-cell">
                      Property
                    </GlassTableHeaderCell>
                    <GlassTableHeaderCell>Status</GlassTableHeaderCell>
                    <GlassTableHeaderCell aria-label="Actions" />
                  </GlassTableRow>
                </GlassTableHead>
                <GlassTableBody>
                  {filtered.map((contact) => {
                    const variant = contactTypeBadgeVariant[contact.contactType] ?? 'default';
                    const label = contactTypeLabel[contact.contactType] ?? contact.contactType;
                    return (
                      <GlassTableRow
                        key={String(contact.id)}
                        className="cursor-pointer"
                        onClick={() => {
                          setEditContact(contact);
                        }}
                      >
                        <GlassTableCell>
                          <span className="font-medium text-sm text-text">{fullName(contact)}</span>
                        </GlassTableCell>
                        <GlassTableCell>
                          <GlassBadge variant={variant}>{label}</GlassBadge>
                        </GlassTableCell>
                        <GlassTableCell className="text-text-secondary hidden md:table-cell">
                          {contact.email !== null ? (
                            <a
                              href={`mailto:${contact.email}`}
                              className="hover:text-primary transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                            >
                              {contact.email}
                            </a>
                          ) : (
                            <span className="text-text-tertiary">&mdash;</span>
                          )}
                        </GlassTableCell>
                        <GlassTableCell className="text-text-secondary hidden lg:table-cell whitespace-nowrap">
                          {contact.phone ?? <span className="text-text-tertiary">&mdash;</span>}
                        </GlassTableCell>
                        <GlassTableCell className="text-text-secondary hidden xl:table-cell">
                          <span className="line-clamp-1">
                            {contact.property?.name ?? (
                              <span className="text-text-tertiary">&mdash;</span>
                            )}
                          </span>
                        </GlassTableCell>
                        <GlassTableCell>
                          <GlassBadge variant={contact.active !== 0 ? 'success' : 'danger'}>
                            {contact.active !== 0 ? 'Active' : 'Inactive'}
                          </GlassBadge>
                        </GlassTableCell>
                        <GlassTableCell className="text-right">
                          <GlassButton
                            variant="ghost"
                            size="sm"
                            aria-label={`Edit ${fullName(contact)}`}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                              e.stopPropagation();
                              setEditContact(contact);
                            }}
                          >
                            Edit
                          </GlassButton>
                        </GlassTableCell>
                      </GlassTableRow>
                    );
                  })}
                </GlassTableBody>
              </GlassTable>
            </div>

            {/* Cards — mobile */}
            <div className="sm:hidden p-4 space-y-3">
              {filtered.map((contact) => (
                <ContactCard key={String(contact.id)} contact={contact} onEdit={setEditContact} />
              ))}
            </div>
          </>
        )}

        {/* Footer count */}
        {!contactsLoading && filtered.length > 0 && (
          <div className="px-5 py-3 border-t border-[var(--glass-border-subtle)]">
            <p className="text-xs text-text-tertiary">
              Showing {String(filtered.length)} of {String(allContacts.length)} contacts
            </p>
          </div>
        )}
      </GlassCard>

      {/* Edit modal */}
      {editContact !== null && (
        <EditModal
          contact={editContact}
          onClose={() => {
            setEditContact(null);
          }}
          onSave={handleEditSave}
          saving={updateLoading}
          saveError={updateError}
        />
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateModal
          properties={properties}
          clientCode={clientCode}
          onClose={() => {
            setShowCreate(false);
          }}
          onSave={handleCreateSave}
          saving={createLoading}
          saveError={createError}
        />
      )}
    </div>
  );
}
